import Vue from "vue";
import Vuex from "vuex";
import VuexPersistence from "vuex-persist";
import jwtDecode from "jwt-decode";
import { getAuth0 } from "@/auth/auth0";

Vue.use(Vuex);

const root = {
  namespaced: true,
  state: {
    maintenanceCode: "snapcrackle",
    maintenanceBegin: 1637233200000,
    maintenanceEnded: 1637272800000,
    apiUrl: process.env.VUE_APP_API_URL,
    redirectUri: process.env.VUE_APP_AUTH_REDIRECT_URI,
    rootLoading: false,
  },
  mutations: {
    loading(state, update) {
      state.rootLoading = update;
    },
  },
  actions: {},
  getters: {
    maintenanceBegin(state) {
      return state.maintenanceBegin;
    },
    maintenanceEnded(state) {
      return state.maintenanceEnded;
    },
    maintenanceCode(state) {
      return state.maintenanceCode;
    },
    rootLoading(state) {
      return state.rootLoading;
    },
    apiUrl(state) {
      return state.apiUrl;
    },
    redirectUri(state) {
      return state.redirectUri;
    },
  },
};

const app = {
  namespaced: true,
  state: {
    appLoading: false,
    selectedTeamId: null,
    selectedCompetitionId: null,
  },
  mutations: {
    loading(state, update) {
      state.appLoading = update;
    },
    selectTeam(state, teamId) {
      state.selectedTeamId = teamId;
    },
    selectCompetition(state, competitionId) {
      state.selectedCompetitionId = competitionId;
    },
  },
  getters: {
    appLoading(state) {
      return state.appLoading;
    },
  },
};

const user = {
  namespaced: true,
  state: {
    maintenanceCleared: false,
    authenticated: false,
    activeRole: false,
    tokens: {},
    profile: {},
  },
  mutations: {
    DEBUGGING(state, update) {
      state.maintenanceCleared = update;
    },
    login(state, tokens) {
      const updateuser = jwtDecode(tokens.id_token);
      const filteredRoles = updateuser.roles.filter(
        (role) =>
          role.type === 5 ||
          role.type === 6 ||
          role.type === 50 ||
          role.type === 99 ||
          role.type === 105
      );
      state.profile = { ...updateuser, roles: filteredRoles };
      state.tokens = tokens;
      state.authenticated = true;
      // Set First Role as Active
      
      if(!state.activeRole){
        state.activeRole = filteredRoles && filteredRoles.length > 0 ? filteredRoles[0] : false;
      }
    },
    async logout(state) {
      state.authenticated = false;
      state.tokens = {};
      state.profile = {};
      state.activeRole = false;
      const auth0 = getAuth0();
      const isAuth = await auth0.isAuthenticated()
      isAuth && await auth0.logout({
        logoutParams: {
          returnTo: process.env.VUE_APP_AUTH_REDIRECT_URI,
        }
      });
    },
    switchRole(state, role) {
        state.activeRole = role;
    },
    updateRoles(state, roles) {
      state.profile.roles =
        roles && roles.length > 0
          ? roles.filter(
              (role) =>
                role.type === 5 ||
                role.type === 6 ||
                role.type === 50 ||
                role.type === 99 ||
                role.type === 105
            )
          : [];
    },
    updateVerification(state, status) {
      state.profile.verified = status;
    },
  },
  actions: { 
    switchRole({ commit }, role) {
    commit('switchRole', role);
    }
  },
  getters: {
    maintenanceCleared(state) {
      return state.maintenanceCleared;
    },
    isAuthenticated(state) {
      return state.authenticated;
    },
    userid(state) {
      return state.profile._id;
    },
    isVerified(state) {
      return state.profile.verified;
    },
    email(state) {
      return state.profile.email;
    },
    expiry(state) {
      return state.tokens.expires_in;
    },
    accessToken(state) {
      return state.tokens.access_token;
    },
    refreshToken(state) {
      return state.tokens.refresh_token;
    },
    userName(state) {
      return `${state.profile.firstName}`;
    },
    lastName(state) {
      return `${state.profile.lastName}`;
    },
    activeRole(state) {
      return state.activeRole;
    },
    activeRoleId(state) {
      return state.activeRole._id;
    },
    roles(state) {
      return state.profile.roles;
    },
    uploadHeaders(state) {
      const { profile, tokens, activeRole } = state;
      const authheader = `${profile._id}:${tokens.access_token}`;
      const encodedString = btoa(authheader);
      const Authorization = `Bearer ${encodedString}`;
      return { Authorization, "active-role": activeRole._id };
    },
  },
};

const randInt = (min, max) => {
  const localMin = Math.ceil(min);
  const localMax = Math.floor(max);
  return Math.floor(Math.random() * (localMax - localMin)) + localMin;
};

const views = {
  namespaced: true,
  state: {
    notifications: [], // { id, msg, route, type }
  },
  mutations: {
    PUSH_NOTIFICATION(state, { msg, route, type, id }) {
      state.notifications = [
        {
          id,
          msg,
          route,
          type,
        },
      ];
    },
    REMOVE_NOTIFICATION(state, id) {
      state.notifications = state.notifications.filter((notification) => notification.id !== id);
    },
    CLEAR_NOTIFICATION(state, route) {
      state.notifications = state.notifications.filter(
        (notification) => notification.route === route
      );
    },
  },
  actions: {
    notify({ commit }, { msg, route, type, autoClose = false, window }) {
      const id = randInt(1, 999999);
      commit("PUSH_NOTIFICATION", {
        msg,
        route,
        type,
        id,
      });
      if (autoClose) {
        setTimeout(() => {
          commit("REMOVE_NOTIFICATION", id);
        }, 5000);
      }
      if (window) window.scrollTo(0, 0);
    },
  },
  getters: {},
};

const vuexLocal = new VuexPersistence({
  key: "nrl-user",
  storage: window.localStorage,
  modules: ["user"],
});

const store = new Vuex.Store({
  modules: {
    root,
    user,
    views,
    app,
  },
  plugins: [vuexLocal.plugin],
});

export default store;
